import { useState } from "react";
import { usePosts } from "../contexts/PostContext";
import { useUser } from "../contexts/UserContext";
import { Post } from "../types/board";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../supabase/supabaseClient";
import TiptapEditor from "../components/TiptapEditor";
import {
  ABTALK_CATEGORIES,
  ANONYMOUS_CATEGORIES,
  FREETALK_CATEGORIES,
} from "../constants/categories";

export interface BoardWriteProps {
  boardType: string;
  onClose: (success?: boolean) => void; // ✅ 성공 여부 전달
  categories?: string[];
  onSubmit?: (post: any) => void;
}

export default function BoardWrite({ boardType, onClose, categories, onSubmit }: BoardWriteProps) {
  const { addPost } = usePosts();
  const { user } = useUser();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // ✅ 로딩/중복방지
  const [poll, setPoll] = useState<{ question: string; options: string[]; deadline?: string } | null>(null);

  // ----------------- 카테고리 설정 -----------------
  let categoryList: { code: string; label: string }[] = [];
  if (boardType === "익명게시판") {
    categoryList = ANONYMOUS_CATEGORIES;
  } else if (
    ["artist", "worker", "fan"].includes(boardType) ||
    boardType.startsWith("abtalk")
  ) {
    categoryList = ABTALK_CATEGORIES;
  } else if (
    boardType === "Free-Talk" ||
    boardType.toLowerCase() === "free-talk" ||
    boardType.toLowerCase() === "freetalk"
  ) {
    categoryList = FREETALK_CATEGORIES;
  }

  const isCommon = boardType === "공동게시판";
  const [category, setCategory] = useState(isCommon ? "" : categoryList[0]?.code || "");
  // -------------------------------------------------

  function getSafeFileName(file: File) {
    const ext = file.name.split('.').pop();
    return `public/img_${uuidv4()}.${ext}`;
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>, editor: any) {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setImageUploading(true);

    const safeName = getSafeFileName(file);
    const { data, error } = await supabase.storage.from("editor-images").upload(safeName, file);
    setImageUploading(false);

    if (!error && data?.path) {
      const { publicUrl } = supabase.storage.from("editor-images").getPublicUrl(data.path).data;
      if (publicUrl) editor.chain().focus().setImage({ src: publicUrl }).run();
      else alert("이미지 url 생성 오류!");
    } else {
      alert("이미지 업로드 실패: " + (error?.message || ""));
    }
  }

  function getFlagsFromContent(html: string) {
    const hasImage = /<img[\s\S]*?>/i.test(html);
    const hasYoutube = /(?:youtube\.com|youtu\.be)/i.test(html);
    const hasVideo = /<video[\s\S]*?>/i.test(html) || hasYoutube;
    return { has_image: hasImage, has_youtube: hasYoutube, has_video: hasVideo };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return; // ✅ 중복 제출 방지

    if (!title.trim()) return alert("제목을 입력하세요!");
    if (!content.trim()) return alert("내용을 입력하세요!");
    if (!user) return alert("로그인 후 글을 작성할 수 있습니다.");

    setSubmitting(true);
    try {
      const flags = getFlagsFromContent(content);

      const newPost = {
        board_type: boardType, // "Free-Talk"로 호출됨
        title,
        content,
        nickname: user.nickname,
        author_id: user.id,
        views: 0,
        likes: 0,
        date: new Date().toISOString(),
        ...(isCommon ? {} : { category }), // ✅ Free-Talk 포함
        ...(poll ? { poll } : {}),
        ...flags, // has_image / has_video / has_youtube
      } as Post;

      if (onSubmit) {
        await onSubmit(newPost);
      } else {
        await addPost(newPost); // ✅ 반드시 await
      }

      // ✅ 사용자 피드백(원하면 토스트로 교체)
      // alert("게시글이 등록되었습니다.");

      onClose(true); // ✅ 성공 신호
    } catch (err: any) {
      console.error(err);
      alert("작성 중 오류가 발생했습니다.\n" + (err?.message || ""));
      // 실패 시 닫지 않음
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded shadow max-w-2xl mx-auto flex flex-col gap-5">
      {/* 카테고리 드롭다운: 공동게시판 제외 */}
      {!isCommon && categoryList.length > 0 && (
        <div>
          <label className="font-semibold mb-1 block">카테고리</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="border rounded px-2 py-1"
          >
            {categoryList.map(cat =>
              <option key={cat.code} value={cat.code}>{cat.label}</option>
            )}
          </select>
        </div>
      )}

      <div>
        <label className="font-semibold mb-1 block">제목</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          className="border rounded w-full px-3 py-2"
          placeholder="제목을 입력하세요"
          maxLength={60}
        />
      </div>

      <div>
        <label className="font-semibold mb-1 block">내용</label>
        <TiptapEditor
          content={content}
          setContent={setContent}
          imageUploading={imageUploading}
          onPollChange={setPoll}
        />
      </div>

      <div className="flex gap-3 justify-end">
        <button type="button" className="px-4 py-2 rounded border" onClick={() => onClose(false)}>취소</button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-black text-white disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "작성 중..." : "작성하기"}
        </button>
      </div>
    </form>
  );
}
